import React, { useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Tooltip,
  IconButton,
  Alert,
  Snackbar,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { 
  Save, 
  Close, 
  Check, 
  Refresh,
  Edit,
  Visibility,
  Download,
  CheckCircle,
  Error,
} from '@mui/icons-material';
import ModalMAIQ from 'app/NewAIAvatarLayout/sharedComponents/ModalMAIQ';
import DownloadModalMAIQ from 'app/NewAIAvatarLayout/sharedComponents/DownloadModalMAIQ';
import InPlaceEditableEmailPreviewMAIQ from 'app/NewAIAvatarLayout/sharedComponents/InPlaceEditableEmailPreviewMAIQ';
import { ConfirmationDialogMAIQ } from 'app/components/ReusableUIComponents/LowLevelComponents/ConfirmationDialogMAIQ';
import { GenerateEmailResponse } from 'app/NewAIAvatarLayout/features/MiaChat/types';
import { useEmailTemplate } from 'app/context/EmailTemplateContext';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import { useTemplateTypes } from 'app/pages/EmailCreateEditPage/hooks/useTemplateTypes';

function formatTypeLabel(value: string): string {
  if (!value) return value;
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

interface EmailPreviewModalProps {
  open: boolean;
  onClose: () => void;
  generatedEmail: GenerateEmailResponse | null;
  onGeneratedEmailChange?: (email: GenerateEmailResponse) => void;
  onSaveTemplateClick?: () => void;
  onTemplateSaveSuccess?: (templateId: string, conversationId: string) => Promise<void>;
  conversationId?: string;
  trackingId?: string;
  emailContent?: { subject: string; sections: any[] };
}

const ModalContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.background.paper,
  overflow: 'hidden',
}));

const ModalHeader = styled(Box)(({ theme }) => ({
  p: 2,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  zIndex: 10,
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  color: theme.palette.primary.contrastText,
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const ContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
}));

const ToolbarContainer = styled(Box)(({ theme }) => ({
  p: 2,
  borderBottom: 1,
  borderColor: 'divider',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: 'background.paper',
  flexShrink: 0,
  width: '100%',
}));

const ToolbarTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.125rem',
  fontWeight: 600,
}));

const ToolbarActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: 1,
}));

const EmailContentArea = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflow: 'auto',
  justifyContent: 'center',
  alignItems: 'center',
  p: 2,
  width: '100%',
}));

const EmailPreviewModal: React.FC<EmailPreviewModalProps> = ({
  open,
  onClose,
  generatedEmail,
  onGeneratedEmailChange,
  onSaveTemplateClick,
  onTemplateSaveSuccess,
  conversationId,
  trackingId,
  emailContent,
}) => {
  const theme = useTheme();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableHtmlContent, setEditableHtmlContent] = useState('');
  const [originalGeneratedHtml, setOriginalGeneratedHtml] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');
  const [downloadFileName, setDownloadFileName] = useState('');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
  });

  // Save template functionality
  const { createEmailTemplate } = useEmailTemplate();
  const selectedDealer = useSelector(selectDealerSelected);
  const selectedDealerId = selectedDealer?.dealer || '';
  const { types: templateTypeOptions, loading: templateTypesLoading } = useTemplateTypes();
  
  // Template details state - directly editable in main modal
  const [templateDetails, setTemplateDetails] = useState({
    name: '',
    type: 'DEALER_VALUE',
    subject: '',
  });
  const [templateFormError, setTemplateFormError] = useState<any>({});
  const [templateLoading, setTemplateLoading] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showEmailDetails, setShowEmailDetails] = useState(false);

  // Helper: match meta template type to getTemplateTypes API value
  const matchTemplateTypeFromApi = useCallback((metaValue: string, apiTypes: string[]): string | null => {
    if (!metaValue || !apiTypes.length) return null;
    const normalize = (s: string) => s.toUpperCase().replace(/\s+/g, '_').trim();
    const metaN = normalize(metaValue);
    return apiTypes.find((t) => normalize(t) === metaN) ?? null;
  }, []);

  // Initialize editable content when modal opens or generatedEmail changes
  React.useEffect(() => {
    if (generatedEmail?.generatedHtml) {
      setEditableHtmlContent(generatedEmail.generatedHtml);
      if (!originalGeneratedHtml) {
        setOriginalGeneratedHtml(generatedEmail.generatedHtml);
      }

      // Initialize template details from emailPreview.meta if available
      let templateName = '';
      let templateType = templateTypeOptions.length > 0 ? templateTypeOptions[0] : 'DEALER_VALUE';
      let subject = '';

      // Check if emailPreview.meta is available
      if (generatedEmail.emailPreview?.meta) {
        const meta = generatedEmail.emailPreview.meta;
        console.log('📧 EmailPreview Meta Data:', meta);
        templateName = meta.name || '';
        const apiTemplateType = meta['template type'] || meta['template_type'] || '';
        // Prefer type from getTemplateTypes API when available
        if (templateTypeOptions.length > 0) {
          const matched = matchTemplateTypeFromApi(apiTemplateType, templateTypeOptions);
          templateType = matched ?? templateTypeOptions[0];
        } else {
          // Fallback mapping when API types not loaded yet
          switch (apiTemplateType.toUpperCase()) {
            case 'OEM VALUE':
            case 'OEM_VALUE':
              templateType = 'OEMV';
              break;
            case 'DEALER VALUE':
            case 'DEALER_VALUE':
              templateType = 'DEALER_VALUE';
              break;
            case 'SINGLE VEHICLE':
            case 'SINGLE_VEHICLE':
              templateType = 'SINGLE_VEHICLE';
              break;
            case 'MULTIPLE VEHICLES':
            case 'MULTIPLE_VEHICLES':
              templateType = 'MULTIPLE_VEHICLES';
              break;
            case 'LEASE VEHICLE':
            case 'LEASE_VEHICLE':
              templateType = 'LEASE_VEHICLE';
              break;
            default:
              templateType = 'DEALER_VALUE';
          }
        }
        subject = meta.subject || '';
      } else {
        // Fallback to original logic if emailPreview.meta is not available
        subject = emailContent?.subject || generatedEmail.subject || '';
        templateName = generatedEmail.subject || '';
        if (templateName.length > 50) {
          templateName = templateName.substring(0, 47) + '...';
        }
        if (!templateName.trim()) {
          const now = new Date();
          const dateStr = now.toLocaleDateString();
          templateName = `Generated Email Template - ${dateStr}`;
        }
      }

      setTemplateDetails({
        name: templateName,
        type: templateType,
        subject: subject,
      });
    }
  }, [generatedEmail, originalGeneratedHtml, emailContent, templateTypeOptions, matchTemplateTypeFromApi]);

  // Handle HTML content change
  const handleHtmlChange = useCallback((newHtml: string) => {
    setEditableHtmlContent(newHtml);
    
    // Always update the generatedEmail state immediately when HTML changes
    if (generatedEmail && onGeneratedEmailChange) {
      onGeneratedEmailChange({
        ...generatedEmail,
        generatedHtml: newHtml
      });
    }
  }, [generatedEmail, onGeneratedEmailChange]);

  // Handle reset to original HTML
  const handleResetToOriginal = useCallback(() => {
    if (originalGeneratedHtml) {
      setEditableHtmlContent(originalGeneratedHtml);
      
      // Also update the generatedEmail state to original
      if (generatedEmail && onGeneratedEmailChange) {
        onGeneratedEmailChange({
          ...generatedEmail,
          generatedHtml: originalGeneratedHtml
        });
      }
    }
  }, [originalGeneratedHtml, generatedEmail, onGeneratedEmailChange]);

  // Handle edit/preview toggle
  const handleEditPreviewToggle = useCallback(() => {
    setIsEditMode(!isEditMode);
    setShowEmailDetails(false); // Hide email details when switching modes
  }, [isEditMode]);

  // Handle email details toggle
  const handleEmailDetailsToggle = useCallback(() => {
    setShowEmailDetails(!showEmailDetails);
  }, [showEmailDetails]);

  // Handle download generated email as HTML file
  const handleDownloadEmail = useCallback(() => {
    setShowEmailDetails(false); // Hide email details when downloading
    if (!generatedEmail?.generatedHtml) {
      setNotification({
        open: true,
        message: 'No generated email content available to download.',
        severity: 'error',
      });
      return;
    }

    try {
      // Generate timestamp in format: YYYY-MM-DD_HH-MM-SS
      const now = new Date();
      const timestamp = now.toISOString()
        .replace(/[-:]/g, '')
        .replace(/\.\d{3}Z$/, '')
        .replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/, '$1-$2-$3_$4-$5-$6');

      // Create filename: generatedemail-YYYY-MM-DD_HH-MM-SS.html
      const filename = `generatedemail-${timestamp}.html`;

      // Create blob with HTML content
      const blob = new Blob([generatedEmail.generatedHtml], { type: 'text/html;charset=utf-8' });
      
      // Create blob URL for DownloadMAIQ
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Set state for DownloadMAIQ modal
      setDownloadUrl(blobUrl);
      setDownloadFileName(filename);
      setShowDownloadModal(true);

    } catch (error: any) {
      console.error('Error preparing email download:', error);
      setNotification({
        open: true,
        message: 'Failed to prepare email for download. Please try again.',
        severity: 'error',
      });
    }
  }, [generatedEmail]);

  // Handle close download modal
  const handleCloseDownloadModal = useCallback(() => {
    setShowDownloadModal(false);
    // Clean up blob URL when modal closes
    if (downloadUrl) {
      window.URL.revokeObjectURL(downloadUrl);
      setDownloadUrl('');
      setDownloadFileName('');
    }
  }, [downloadUrl]);

  // Handle notification close
  const handleNotificationClose = useCallback(() => {
    setNotification(prev => ({ ...prev, open: false }));
  }, []);

  // Show save confirmation modal
  const handleSaveTemplateClick = useCallback(() => {
    if (!generatedEmail) {
      setNotification({
        open: true,
        message: 'No email content available to save as template.',
        severity: 'error',
      });
      return;
    }

    // Validate template details
    const errors: any = {};
    if (!templateDetails.name.trim()) {
      errors.name = 'Template name is required';
    }
    if (!templateDetails.subject.trim()) {
      errors.subject = 'Subject is required';
    }
    if (!templateDetails.type.trim()) {
      errors.type = 'Template type is required';
    }
    
    setTemplateFormError(errors);
    if (Object.keys(errors).length > 0) {
      setNotification({
        open: true,
        message: 'Please fill in all required fields before saving the template.',
        severity: 'error',
      });
      return;
    }

    if (!selectedDealerId) {
      setNotification({
        open: true,
        message: 'Please select a dealer first before saving the template.',
        severity: 'error',
      });
      return;
    }

    // Show confirmation modal
    setShowSaveConfirmation(true);
  }, [generatedEmail, templateDetails, selectedDealerId]);

  // Actually save template after confirmation
  const handleConfirmSave = useCallback(async () => {
    setTemplateLoading(true);
    
    try {
      const savedTemplate = await createEmailTemplate(selectedDealerId, {
        type: templateDetails.type,
        name: templateDetails.name,
        subject: templateDetails.subject,
        html: editableHtmlContent || generatedEmail?.generatedHtml || '',
        ...(trackingId && { metadata: { tracking_id: trackingId } }),
      });

      const templateId = savedTemplate?.id || (savedTemplate as any)?._id || (savedTemplate as any)?.template_id;

      if (onTemplateSaveSuccess && templateId && conversationId) {
        await onTemplateSaveSuccess(templateId, conversationId);
      }

      setShowSaveConfirmation(false);
      setNotification({
        open: true,
        message: `Template "${templateDetails.name}" saved successfully!`,
        severity: 'success',
      });
    } catch (error: any) {
      console.error('Error saving template:', error);
      setNotification({
        open: true,
        message: error?.message || 'Failed to save template. Please try again.',
        severity: 'error',
      });
    } finally {
      setTemplateLoading(false);
    }
  }, [selectedDealerId, templateDetails, editableHtmlContent, generatedEmail, onTemplateSaveSuccess, conversationId, trackingId, createEmailTemplate]);

  // Cancel save confirmation
  const handleCancelSave = useCallback(() => {
    if (templateLoading) return;
    setShowSaveConfirmation(false);
  }, [templateLoading]);


  // Handle modal close
  const handleModalClose = useCallback(() => {
    setIsEditMode(false);
    onClose();
  }, [onClose]);

  return (
    <>
      <ModalMAIQ
        open={open}
        onClose={handleModalClose}
        customStyle={{
          width: '100%',
          maxWidth: showEmailDetails ? '80vw' : '60vw',
          height: '90vh',
          maxHeight: '90vh',
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}
      >
            <ModalContainer>
              {/* Header Section with Title Only */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-start',
                alignItems: 'center',
                p: theme.spacing(2, 3),
                backgroundColor: theme.palette.background.paper,
                borderBottom: `1px solid ${theme.palette.divider}`,
                minHeight: '64px',
                flexShrink: 0
              }}>
                {/* Dynamic Title */}
                <Typography 
                  variant="h6" 
                  sx={{
                    fontWeight: 600,
                    fontSize: theme.typography.h6.fontSize,
                    color: theme.palette.text.primary,
                    fontFamily: theme.typography.fontFamily
                  }}
                >
                  {isEditMode ? 'Email Edit' : 'Email Preview'}
                </Typography>
              </Box>

              {/* Main Content Area - Responsive Split Layout */}
              <Box sx={{
                flex: 1,
                display: 'flex',
                overflow: 'hidden',
                flexDirection: { xs: 'column', md: 'row' }
              }}>
                {/* Left Panel - Email Preview/Edit */}
                <Box sx={{
                  flex: { xs: '1 1 auto', md: showEmailDetails ? '0 0 65%' : '1 1 auto' },
                  display: 'flex',
                  flexDirection: 'column',
                  borderRight: { xs: 'none', md: showEmailDetails ? `1px solid ${theme.palette.divider}` : 'none' },
                  borderBottom: { xs: `1px solid ${theme.palette.divider}`, md: 'none' },
                  minHeight: { xs: '50vh', md: 'auto' }
                }}>

                  <Box sx={{
                    flex: 1,
                    overflow: 'hidden',
                    p: theme.spacing(2),
                    backgroundColor: theme.palette.background.paper
                  }}>
                    {generatedEmail ? (
                      isEditMode ? (
                        <Box sx={{ height: '100%', overflow: 'auto' }}>
                          <InPlaceEditableEmailPreviewMAIQ
                            htmlContent={editableHtmlContent}
                            onHtmlChange={handleHtmlChange}
                            isEditable={true}
                          />
                        </Box>
                      ) : (
                        <iframe
                          srcDoc={generatedEmail.generatedHtml}
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            fontFamily: theme.typography.fontFamily,
                            fontSize: theme.typography.body1.fontSize,
                            lineHeight: theme.typography.body1.lineHeight,
                            backgroundColor: theme.palette.background.paper,
                          }}
                          sandbox="allow-same-origin"
                          title="Email Preview"
                        />
                      )
                    ) : (
                      <Box sx={{
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                        gap: theme.spacing(2)
                      }}>
                        <Typography 
                          variant="h6" 
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: theme.typography.h6.fontSize,
                            fontFamily: theme.typography.fontFamily
                          }}
                        >
                          No email content available
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: theme.typography.body2.fontSize,
                            fontFamily: theme.typography.fontFamily
                          }}
                        >
                          Generate an email first to preview it here
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                {/* Right Panel - Email Details */}
                {showEmailDetails && (
                  <Box sx={{
                    flex: { xs: '1 1 auto', md: '0 0 35%' },
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: { xs: '50vh', md: 'auto' }
                  }}>
                  <Box sx={{
                    p: theme.spacing(2),
                    backgroundColor: theme.palette.background.default,
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}>
                    <Typography 
                      variant="body1" 
                      sx={{
                        fontWeight: 600,
                        fontSize: theme.typography.body1.fontSize,
                        color: theme.palette.text.primary,
                        fontFamily: theme.typography.fontFamily
                      }}
                    >
                      Email Details
                    </Typography>
                  </Box>

                  <Box sx={{
                    flex: 1,
                    overflow: 'auto',
                    p: theme.spacing(3),
                    backgroundColor: theme.palette.background.paper,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(3)
                  }}>
                    {/* Template Name */}
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{
                          fontWeight: 500,
                          fontSize: theme.typography.body2.fontSize,
                          color: theme.palette.text.primary,
                          fontFamily: theme.typography.fontFamily,
                          mb: theme.spacing(1)
                        }}
                      >
                        Template Name *
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={templateDetails.name}
                        onChange={(e) => {
                          setTemplateDetails(prev => ({ ...prev, name: e.target.value }));
                        }}
                        error={!!templateFormError.name}
                        helperText={templateFormError.name}
                        placeholder="Enter template name"
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: theme.typography.body1.fontSize,
                            fontFamily: theme.typography.fontFamily
                          }
                        }}
                      />
                    </Box>

                    {/* Type - same as Create Email Template page (getTemplateTypes) */}
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{
                          fontWeight: 500,
                          fontSize: theme.typography.body2.fontSize,
                          color: theme.palette.text.primary,
                          fontFamily: theme.typography.fontFamily,
                          mb: theme.spacing(1)
                        }}
                      >
                        Type *
                      </Typography>
                      <FormControl fullWidth size="small" error={!!templateFormError.type}>
                        <Select
                          value={templateDetails.type}
                          onChange={(e) => setTemplateDetails(prev => ({ ...prev, type: e.target.value }))}
                          displayEmpty
                          disabled={templateTypesLoading}
                          renderValue={(value) => (value ? formatTypeLabel(value) : '')}
                          sx={{
                            fontSize: theme.typography.body1.fontSize,
                            fontFamily: theme.typography.fontFamily
                          }}
                        >
                          <MenuItem value="" disabled>
                            {templateTypesLoading ? 'Loading types...' : 'Select type'}
                          </MenuItem>
                          {templateTypeOptions.map((type) => (
                            <MenuItem key={type} value={type}>
                              {formatTypeLabel(type)}
                            </MenuItem>
                          ))}
                        </Select>
                        {templateFormError.type && (
                          <Typography 
                            variant="caption" 
                            color="error" 
                            sx={{ 
                              mt: theme.spacing(0.5), 
                              ml: theme.spacing(1.5),
                              fontSize: theme.typography.body2.fontSize,
                              fontFamily: theme.typography.fontFamily
                            }}
                          >
                            {templateFormError.type}
                          </Typography>
                        )}
                      </FormControl>
                    </Box>

                    {/* Email Subject */}
                    <Box>
                      <Typography 
                        variant="body2" 
                        sx={{
                          fontWeight: 500,
                          fontSize: theme.typography.body2.fontSize,
                          color: theme.palette.text.primary,
                          fontFamily: theme.typography.fontFamily,
                          mb: theme.spacing(1)
                        }}
                      >
                        Email Subject *
                      </Typography>
                      <TextField
                        fullWidth
                        variant="outlined"
                        value={templateDetails.subject}
                        onChange={(e) => {
                          setTemplateDetails(prev => ({ ...prev, subject: e.target.value }));
                        }}
                        error={!!templateFormError.subject}
                        helperText={templateFormError.subject}
                        placeholder="Enter email subject"
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            fontSize: theme.typography.body1.fontSize,
                            fontFamily: theme.typography.fontFamily
                          }
                        }}
                      />
                    </Box>

                    {/* Tracking ID - subtle, below Email Subject */}
                    {trackingId && (
                      <Typography
                        variant="caption"
                        sx={{
                          fontStyle: 'italic',
                          color: theme.palette.text.secondary,
                          fontFamily: theme.typography.fontFamily,
                        }}
                      >
                        Tracking ID: {trackingId}
                      </Typography>
                    )}
                  </Box>
                </Box>
                )}
              </Box>

              {/* Bottom Action Buttons */}
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: theme.spacing(2),
                p: theme.spacing(2, 3),
                backgroundColor: theme.palette.background.paper,
                borderTop: `1px solid ${theme.palette.divider}`,
                flexShrink: 0
              }}>
                <Button
                  variant="outlined"
                  onClick={handleEditPreviewToggle}
                  startIcon={isEditMode ? <Visibility /> : <Edit />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    py: 0.75,
                    px: 3,
                    minWidth: '120px',
                    fontSize: theme.typography.body1.fontSize,
                    fontFamily: theme.typography.fontFamily,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main
                    }
                  }}
                >
                  {isEditMode ? 'Preview' : 'Edit'}
                </Button>

                <Button
                  variant="outlined"
                  onClick={handleDownloadEmail}
                  disabled={!generatedEmail?.generatedHtml}
                  startIcon={<Download />}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    borderColor: theme.palette.divider,
                    py: 0.75,
                    px: 3,
                    minWidth: '140px',
                    fontSize: theme.typography.body1.fontSize,
                    fontFamily: theme.typography.fontFamily,
                    '&:hover': {
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main
                    },
                    '&:disabled': {
                      opacity: 0.5
                    }
                  }}
                >
                  Download
                </Button>

                {!showEmailDetails ? (
                  <Button
                    variant="outlined"
                    onClick={handleEmailDetailsToggle}
                    startIcon={<Save />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      borderColor: theme.palette.divider,
                      py: 0.75,
                      px: 3,
                      minWidth: '160px',
                      fontSize: theme.typography.body1.fontSize,
                      fontFamily: theme.typography.fontFamily,
                      '&:hover': {
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.main
                      }
                    }}
                  >
                    Save as Template
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleSaveTemplateClick}
                    disabled={templateLoading}
                    startIcon={<Save />}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      bgcolor: '#000000',
                      color: 'white',
                      py: 0.75,
                      px: 3,
                      minWidth: '160px',
                      fontSize: theme.typography.body1.fontSize,
                      fontFamily: theme.typography.fontFamily,
                      '&:hover': {
                        bgcolor: '#333333',
                      },
                      '&:disabled': {
                        bgcolor: '#666666',
                        opacity: 0.7
                      }
                    }}
                  >
                    {templateLoading ? 'Saving...' : 'Save as Template'}
                  </Button>
                )}
              </Box>
            </ModalContainer>
      </ModalMAIQ>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleNotificationClose}
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>

      {/* Download Modal using DownloadMAIQ */}
      <DownloadModalMAIQ
        open={showDownloadModal}
        onClose={handleCloseDownloadModal}
        downloadUrl={downloadUrl}
        title="Download Generated Email"
        fileName={downloadFileName}
      />

      {/* Save Confirmation Modal */}
      <ConfirmationDialogMAIQ
        open={showSaveConfirmation}
        onClose={handleCancelSave}
        onConfirm={handleConfirmSave}
        title="Save Email Template?"
        message={
          <Box>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to save this email as a template named{' '}
              <strong>&quot;{templateDetails.name}&quot;</strong>?
            </Typography>
            <Box
              sx={{
                backgroundColor: 'action.hover',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 2,
                textAlign: 'left',
              }}
            >
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Template Details:
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Name:</strong> {templateDetails.name}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>Type:</strong> {templateDetails.type}
              </Typography>
              <Typography variant="body2">
                <strong>Subject:</strong> {templateDetails.subject}
              </Typography>
            </Box>
          </Box>
        }
        confirmLabel="Save Template"
        loading={templateLoading}
        loadingLabel="Saving..."
      />

    </>
  );
};

export default EmailPreviewModal;

